import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import "../assets/css/biodata.css";

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction
  ? "https://schools.luth.gov.ng"
  : "http://localhost:3003";

const REQUIRED_FIELDS = {
  fullName: "Full Name",
  dob: "Date of Birth",
  gender: "Gender",
  nationality: "Nationality",
  stateOfOrigin: "State of Origin",
  lga: "LGA",
  religion: "Religion",
  maritalStatus: "Marital Status",
  phone: "Phone Number",
  email: "Email",
  address: "Home Address",
  jambRegNo: "JAMB Registration Number",
  nextOfKin: "Next of Kin Name",
  nextOfKinPhone: "Next of Kin Phone",
  nextOfKinAddress: "Next of Kin Address",
  nextOfKinRelationship: "Next of Kin Relationship",
  nextOfKinEmail: "Next of Kin Email",
  primarySchool: "Primary School",
  primaryGradYear: "Primary Graduation Year",
  primaryQualification: "Primary Qualification",
  secondarySchool: "Secondary School",
  secondaryGradYear: "Secondary Graduation Year",
  secondaryQualification: "Secondary Qualification",
  sponsorName: "Sponsor Name",
  sponsorPhone: "Sponsor Phone 1",
  sponsorEmail: "Sponsor Email",
  sponsorAddress: "Sponsor Address",
  session: "Session",
};

const StudentBioDataForm = ({ initialData = null, isEditMode = false }) => {
  const { matricNo: urlMatric } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    dob: "",
    gender: "",
    maritalStatus: "",
    email: "",
    phone: "",
    address: "",
    matricNo: "",
    department: "",
    calenderYear: "",
    session: "",
    sponsorName: "",
    sponsorPhone: "",
    sponsorPhone2: "",
    sponsorEmail: "",
    sponsorAddress: "",
    nextOfKin: "",
    nextOfKinPhone: "",
    nextOfKinAddress: "",
    nextOfKinRelationship: "",
    nextOfKinEmail: "",
    nationality: "",
    stateOfOrigin: "",
    lga: "",
    religion: "",
    jambRegNo: "",
    primarySchool: "",
    primaryGradYear: "",
    primaryQualification: "",
    secondarySchool: "",
    secondaryGradYear: "",
    secondaryQualification: "",
    postSecondarySchool: "",
    postSecondaryGradYear: "",
    postSecondaryQualification: "",
    otherQualification: "",
    otherQualificationYear: "",
    otherQualificationSchool: "",
    picture: null,
    entryMode: "UTME",
    entryYear: "N/A",
    level: "N/A",
  });

  const [originalPictureUrl, setOriginalPictureUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingBio, setHasExistingBio] = useState(false);

  const fileInputRef = useRef(null);
  const navTimerRef = useRef(null);

  const formatDateForInput = (value) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const dt = new Date(value);
    if (isNaN(dt)) return "";
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDateForDisplay = (value) => {
    if (!value) return "Not set";
    const dt = new Date(value);
    if (isNaN(dt)) return value;
    return dt.toLocaleDateString();
  };

  const getMissingRequiredFields = () => {
    const missing = [];
    Object.keys(REQUIRED_FIELDS).forEach((key) => {
      const value = formData[key];
      if (
        value === null ||
        value === undefined ||
        String(value).trim() === ""
      ) {
        missing.push(REQUIRED_FIELDS[key]);
      }
    });
    return missing;
  };

  useEffect(() => {
    async function fetchUserAndBio() {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const userRes = await axios.get(
          `${API_BASE_URL}/api/users/${urlMatric}`,
          { headers },
        );

        let bioData = null;
        let bioExists = false;

        try {
          const bioRes = await axios.get(
            `${API_BASE_URL}/api/biodata/${urlMatric}`,
            { headers },
          );
          bioData = bioRes.data;
          bioExists = !!bioData;
        } catch (bioErr) {
          bioData = null;
          bioExists = false;
        }

        const user = userRes.data || {};
        const bio = bioData || {};

        setHasExistingBio(bioExists);

        setFormData((prev) => ({
          ...prev,
          fullName: user.fullName || bio.fullName || prev.fullName,
          email: user.email || bio.email || prev.email,
          matricNo: user.matricNo || bio.matricNumber || prev.matricNo,
          department: user.programme || bio.department || prev.department,
          calenderYear:
            user.CalenderYear ||
            bio.CalenderYear ||
            bio.session ||
            prev.calenderYear,
          session:
            bio.session ||
            user.session ||
            user.CalenderYear ||
            bio.CalenderYear ||
            prev.session,
          entryMode: user.entryMode || bio.entryMode || prev.entryMode,
          entryYear: user.entryYear || bio.entryYear || prev.entryYear,
          level: user.StudentLevel || bio.level || prev.level,
          dob: bio.dateOfBirth || prev.dob,
          gender: bio.gender || prev.gender,
          maritalStatus: bio.maritalStatus || prev.maritalStatus,
          phone: bio.phoneNumber || prev.phone,
          address: bio.address || prev.address,
          sponsorName: bio.sponsorName || prev.sponsorName,
          sponsorPhone: bio.sponsorPhone1 || prev.sponsorPhone,
          sponsorPhone2: bio.sponsorPhone2 || prev.sponsorPhone2,
          sponsorEmail: bio.sponsorEmail || prev.sponsorEmail,
          sponsorAddress: bio.sponsorAddress || prev.sponsorAddress,
          nextOfKin: bio.nextOfKinName || prev.nextOfKin,
          nextOfKinPhone: bio.nextOfKinPhone || prev.nextOfKinPhone,
          nextOfKinAddress: bio.nextOfKinAddress || prev.nextOfKinAddress,
          nextOfKinRelationship:
            bio.nextOfKinRelationship || prev.nextOfKinRelationship,
          nextOfKinEmail: bio.nextOfKinEmail || prev.nextOfKinEmail,
          nationality: bio.nationality || prev.nationality,
          stateOfOrigin: bio.stateOfOrigin || prev.stateOfOrigin,
          lga: bio.lga || prev.lga,
          religion: bio.religion || prev.religion,
          jambRegNo: bio.jambRegNumber || prev.jambRegNo,
          primarySchool: bio.primarySchool || prev.primarySchool,
          primaryGradYear: bio.primaryGradYear || prev.primaryGradYear,
          primaryQualification:
            bio.primaryQualification || prev.primaryQualification,
          secondarySchool: bio.secondarySchool || prev.secondarySchool,
          secondaryGradYear: bio.secondaryGradYear || prev.secondaryGradYear,
          secondaryQualification:
            bio.secondaryQualification || prev.secondaryQualification,
          postSecondarySchool:
            bio.postSecondarySchool || prev.postSecondarySchool,
          postSecondaryGradYear:
            bio.postSecondaryGradYear || prev.postSecondaryGradYear,
          postSecondaryQualification:
            bio.postSecondaryQualification || prev.postSecondaryQualification,
          otherQualification: bio.otherQualification || prev.otherQualification,
          otherQualificationYear:
            bio.otherQualificationYear || prev.otherQualificationYear,
          otherQualificationSchool:
            bio.otherQualificationSchool || prev.otherQualificationSchool,
          picture: bio.picture || null,
        }));

        if (bio.picture) setOriginalPictureUrl(bio.picture);
      } catch (err) {
        console.error(err);
        setError("Failed to load profile data.");
      } finally {
        setLoading(false);
      }
    }

    fetchUserAndBio();
  }, [urlMatric]);

  useEffect(() => {
    if (initialData) {
      const pictureVal = initialData.picture || null;

      setHasExistingBio(true);

      setFormData((prev) => ({
        ...prev,
        fullName: initialData.fullName || prev.fullName,
        dob: initialData.dateOfBirth || prev.dob,
        gender: initialData.gender || prev.gender,
        maritalStatus: initialData.maritalStatus || prev.maritalStatus,
        email: initialData.email || prev.email,
        phone: initialData.phoneNumber || prev.phone,
        address: initialData.address || prev.address,
        matricNo: initialData.matricNumber || prev.matricNo,
        department: initialData.department || prev.department,
        calenderYear:
          initialData.CalenderYear ||
          initialData.calenderYear ||
          initialData.session ||
          prev.calenderYear,
        session:
          initialData.session ||
          initialData.CalenderYear ||
          initialData.calenderYear ||
          prev.session,
        sponsorName: initialData.sponsorName || prev.sponsorName,
        sponsorPhone: initialData.sponsorPhone1 || prev.sponsorPhone,
        sponsorPhone2: initialData.sponsorPhone2 || prev.sponsorPhone2,
        sponsorEmail: initialData.sponsorEmail || prev.sponsorEmail,
        sponsorAddress: initialData.sponsorAddress || prev.sponsorAddress,
        nextOfKin: initialData.nextOfKinName || prev.nextOfKin,
        nextOfKinPhone: initialData.nextOfKinPhone || prev.nextOfKinPhone,
        nextOfKinAddress: initialData.nextOfKinAddress || prev.nextOfKinAddress,
        nextOfKinRelationship:
          initialData.nextOfKinRelationship || prev.nextOfKinRelationship,
        nextOfKinEmail: initialData.nextOfKinEmail || prev.nextOfKinEmail,
        nationality: initialData.nationality || prev.nationality,
        stateOfOrigin: initialData.stateOfOrigin || prev.stateOfOrigin,
        lga: initialData.lga || prev.lga,
        religion: initialData.religion || prev.religion,
        jambRegNo: initialData.jambRegNumber || prev.jambRegNo,
        primarySchool: initialData.primarySchool || prev.primarySchool,
        primaryGradYear: initialData.primaryGradYear || prev.primaryGradYear,
        primaryQualification:
          initialData.primaryQualification || prev.primaryQualification,
        secondarySchool: initialData.secondarySchool || prev.secondarySchool,
        secondaryGradYear:
          initialData.secondaryGradYear || prev.secondaryGradYear,
        secondaryQualification:
          initialData.secondaryQualification || prev.secondaryQualification,
        postSecondarySchool:
          initialData.postSecondarySchool || prev.postSecondarySchool,
        postSecondaryGradYear:
          initialData.postSecondaryGradYear || prev.postSecondaryGradYear,
        postSecondaryQualification:
          initialData.postSecondaryQualification ||
          prev.postSecondaryQualification,
        otherQualification:
          initialData.otherQualification || prev.otherQualification,
        otherQualificationYear:
          initialData.otherQualificationsYear ||
          initialData.otherQualificationYear ||
          prev.otherQualificationYear,
        picture: pictureVal,
        entryMode: initialData.entryMode || prev.entryMode,
        entryYear: initialData.entryYear || prev.entryYear,
        level: initialData.level || prev.level,
      }));

      if (pictureVal) setOriginalPictureUrl(pictureVal);
      setLoading(false);
    }
  }, [initialData]);

  useEffect(() => {
    return () => {
      if (navTimerRef.current) {
        clearTimeout(navTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    setFormData((prev) => ({ ...prev, picture: file }));
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const revertToOriginalPicture = () => {
    setFormData((prev) => ({ ...prev, picture: originalPictureUrl || null }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const missing = getMissingRequiredFields();
    if (missing.length > 0) {
      setError(`Please complete the required fields: ${missing.join(", ")}.`);
      return;
    }

    try {
      setSubmitting(true);

      const form = new FormData();

      form.append("fullName", formData.fullName || "");
      form.append("dob", formData.dob || "");
      form.append("gender", formData.gender || "");
      form.append("maritalStatus", formData.maritalStatus || "");
      form.append("email", formData.email || "");
      form.append("phone", formData.phone || "");
      form.append("address", formData.address || "");
      form.append("matricNo", formData.matricNo || "");
      form.append("department", formData.department || "");
      form.append("calenderYear", formData.calenderYear || "");
      form.append("session", formData.session || "");
      form.append("sponsorName", formData.sponsorName || "");
      form.append("sponsorPhone", formData.sponsorPhone || "");
      form.append("sponsorPhone2", formData.sponsorPhone2 || "");
      form.append("sponsorEmail", formData.sponsorEmail || "");
      form.append("sponsorAddress", formData.sponsorAddress || "");
      form.append("nextOfKin", formData.nextOfKin || "");
      form.append("nextOfKinPhone", formData.nextOfKinPhone || "");
      form.append("nextOfKinAddress", formData.nextOfKinAddress || "");
      form.append(
        "nextOfKinRelationship",
        formData.nextOfKinRelationship || "",
      );
      form.append("nextOfKinEmail", formData.nextOfKinEmail || "");
      form.append("nationality", formData.nationality || "");
      form.append("stateOfOrigin", formData.stateOfOrigin || "");
      form.append("lga", formData.lga || "");
      form.append("religion", formData.religion || "");
      form.append("jambRegNo", formData.jambRegNo || "");
      form.append("primarySchool", formData.primarySchool || "");
      form.append("primaryGradYear", formData.primaryGradYear || "");
      form.append("primaryQualification", formData.primaryQualification || "");
      form.append("secondarySchool", formData.secondarySchool || "");
      form.append("secondaryGradYear", formData.secondaryGradYear || "");
      form.append(
        "secondaryQualification",
        formData.secondaryQualification || "",
      );
      form.append("postSecondarySchool", formData.postSecondarySchool || "");
      form.append(
        "postSecondaryGradYear",
        formData.postSecondaryGradYear || "",
      );
      form.append(
        "postSecondaryQualification",
        formData.postSecondaryQualification || "",
      );
      form.append("otherQualification", formData.otherQualification || "");
      form.append(
        "otherQualificationYear",
        formData.otherQualificationYear || "",
      );
      form.append(
        "otherQualificationSchool",
        formData.otherQualificationSchool || "",
      );
      form.append("entryMode", formData.entryMode || "");
      form.append("entryYear", formData.entryYear || "");
      form.append("level", formData.level || "");

      if (formData.picture instanceof File) {
        form.append("picture", formData.picture);
      }

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const shouldUpdate = hasExistingBio;

      if (shouldUpdate) {
        try {
          const putRes = await axios.put(
            `${API_BASE_URL}/api/biodata/${formData.matricNo}`,
            form,
            { headers },
          );

          if (putRes?.status === 200 && putRes?.data?.updated) {
            setShowSuccess(true);
            navTimerRef.current = setTimeout(() => {
              navigate("/student-profile", { replace: true });
            }, 1800);
            return;
          }

          if (putRes?.status === 404) {
            const postRes = await axios.post(
              `${API_BASE_URL}/api/biodata`,
              form,
              {
                headers,
              },
            );

            if (postRes?.status === 201 && postRes?.data?.insertedId) {
              setHasExistingBio(true);
              setShowSuccess(true);
              navTimerRef.current = setTimeout(() => {
                navigate("/student-profile", { replace: true });
              }, 1800);
              return;
            }
          }

          setError("Update completed without a confirmed database update.");
          return;
        } catch (putErr) {
          if (putErr?.response?.status === 404) {
            const postRes = await axios.post(
              `${API_BASE_URL}/api/biodata`,
              form,
              {
                headers,
              },
            );

            if (postRes?.status === 201 && postRes?.data?.insertedId) {
              setHasExistingBio(true);
              setShowSuccess(true);
              navTimerRef.current = setTimeout(() => {
                navigate("/student-profile", { replace: true });
              }, 1800);
              return;
            }
          }

          throw putErr;
        }
      }

      const postRes = await axios.post(`${API_BASE_URL}/api/biodata`, form, {
        headers,
      });

      if (postRes?.status === 201 && postRes?.data?.insertedId) {
        setHasExistingBio(true);
        setShowSuccess(true);
        navTimerRef.current = setTimeout(() => {
          navigate("/student-profile", { replace: true });
        }, 1800);
        return;
      }

      setError("Submission completed without a confirmed database insert.");
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.sqlMessage ||
          err.response?.data?.msg ||
          "Submission failed. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p>Loading profile…</p>;

  let previewSrc = null;
  if (formData.picture instanceof File) {
    try {
      previewSrc = URL.createObjectURL(formData.picture);
    } catch (e) {
      previewSrc = null;
    }
  } else if (typeof formData.picture === "string" && formData.picture) {
    previewSrc = formData.picture;
  }

  return (
    <div className="bio-form-container" style={{ position: "relative" }}>
      {showSuccess && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 16,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: "28px 24px",
              width: "100%",
              maxWidth: 420,
              boxShadow: "0 16px 50px rgba(0,0,0,0.18)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 58,
                height: 58,
                borderRadius: "50%",
                margin: "0 auto 14px",
                display: "grid",
                placeItems: "center",
                background: "#e7f8ee",
                color: "#138a3d",
                fontSize: 28,
                fontWeight: 700,
              }}
            >
              ✓{" "}
            </div>
            <h3 style={{ margin: "0 0 8px" }}>Success</h3>
            <p style={{ margin: 0, color: "#555" }}>
              Bio-data submitted successfully.{" "}
            </p>{" "}
          </div>{" "}
        </div>
      )}

      <h2>{isEditMode ? "Edit Bio-data" : "Student Bio-data Form"}</h2>
      {error && <p className="error-message">{error}</p>}

      <form onSubmit={handleSubmit}>
        <FormSection title="Academic Details">
          <Input
            label="Matric Number"
            name="matricNo"
            value={formData.matricNo}
            onChange={handleChange}
            readOnly
          />
          <Input
            label="Programme"
            name="department"
            value={formData.department}
            onChange={handleChange}
            readOnly
          />
          <Input
            label="Session"
            name="session"
            value={formData.session}
            onChange={handleChange}
            readOnly
            required
          />
          <Input
            label="Calender Year"
            name="calenderYear"
            value={formData.calenderYear}
            onChange={handleChange}
            readOnly
          />
          <Input
            label="Entry Level"
            name="level"
            value={formData.level}
            onChange={handleChange}
            readOnly
          />
          <Input
            label="Entry Mode"
            name="entryMode"
            value={formData.entryMode}
            onChange={handleChange}
            readOnly
          />
          <Input
            label="Entry Year"
            name="entryYear"
            value={formData.entryYear}
            onChange={handleChange}
            readOnly
          />
        </FormSection>

        <FormSection title="Personal Information">
          <Input
            label="Full Name"
            name="fullName"
            value={formData.fullName}
            readOnly
            required
          />

          <DOBField
            label="Date of Birth"
            name="dob"
            value={formData.dob}
            onChange={(newVal) => setFormData((p) => ({ ...p, dob: newVal }))}
            formatDateForDisplay={formatDateForDisplay}
            formatDateForInput={formatDateForInput}
            required
          />

          <Select
            label="Gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            options={["Male", "Female"]}
            required
          />
          <Select
            label="Marital Status"
            name="maritalStatus"
            value={formData.maritalStatus}
            onChange={handleChange}
            options={["Single", "Married"]}
            required
          />
          <Input
            label="Nationality"
            name="nationality"
            value={formData.nationality}
            onChange={handleChange}
            required
          />
          <Input
            label="State of Origin"
            name="stateOfOrigin"
            value={formData.stateOfOrigin}
            onChange={handleChange}
            required
          />
          <Input
            label="LGA"
            name="lga"
            value={formData.lga}
            onChange={handleChange}
            required
          />
          <Input
            label="Religion"
            name="religion"
            value={formData.religion}
            onChange={handleChange}
            required
          />
        </FormSection>

        <FormSection title="Contact Information">
          <Input
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <Input
            label="Phone Number"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            required
          />
          <Input
            label="Home Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
          />
        </FormSection>

        <FormSection title="Academic Information">
          <Input
            label="JAMB Registration Number"
            name="jambRegNo"
            value={formData.jambRegNo}
            onChange={handleChange}
            required
          />
        </FormSection>

        <FormSection title="Sponsor Information">
          <Input
            label="Sponsor Name"
            name="sponsorName"
            value={formData.sponsorName}
            onChange={handleChange}
            required
          />
          <Input
            label="Sponsor Phone 1"
            name="sponsorPhone"
            type="tel"
            value={formData.sponsorPhone}
            onChange={handleChange}
            required
          />
          <Input
            label="Sponsor Phone 2"
            name="sponsorPhone2"
            type="tel"
            value={formData.sponsorPhone2}
            onChange={handleChange}
          />
          <Input
            label="Sponsor Email"
            name="sponsorEmail"
            type="email"
            value={formData.sponsorEmail}
            onChange={handleChange}
            required
          />
          <Input
            label="Sponsor Address"
            name="sponsorAddress"
            value={formData.sponsorAddress}
            onChange={handleChange}
            required
          />
        </FormSection>

        <FormSection title="Next of Kin">
          <Input
            label="Name"
            name="nextOfKin"
            value={formData.nextOfKin}
            onChange={handleChange}
            required
          />
          <Input
            label="Phone"
            name="nextOfKinPhone"
            type="tel"
            value={formData.nextOfKinPhone}
            onChange={handleChange}
            required
          />
          <Input
            label="Address"
            name="nextOfKinAddress"
            value={formData.nextOfKinAddress}
            onChange={handleChange}
            required
          />
          <Input
            label="Email"
            name="nextOfKinEmail"
            type="email"
            value={formData.nextOfKinEmail}
            onChange={handleChange}
            required
          />
          <Input
            label="Relationship"
            name="nextOfKinRelationship"
            value={formData.nextOfKinRelationship}
            onChange={handleChange}
            required
          />
        </FormSection>

        <FormSection title="Educational Background">
          <Input
            label="Primary School"
            name="primarySchool"
            value={formData.primarySchool}
            onChange={handleChange}
            required
          />
          <Input
            label="Graduation Year"
            name="primaryGradYear"
            type="number"
            value={formData.primaryGradYear}
            onChange={handleChange}
            required
          />
          <Input
            label="Qualification"
            name="primaryQualification"
            value={formData.primaryQualification}
            onChange={handleChange}
            required
          />
          <Input
            label="Secondary School"
            name="secondarySchool"
            value={formData.secondarySchool}
            onChange={handleChange}
            required
          />
          <Input
            label="Graduation Year"
            name="secondaryGradYear"
            type="number"
            value={formData.secondaryGradYear}
            onChange={handleChange}
            required
          />
          <Input
            label="Qualification"
            name="secondaryQualification"
            value={formData.secondaryQualification}
            onChange={handleChange}
            required
          />
          <Input
            label="Post-Secondary School"
            name="postSecondarySchool"
            value={formData.postSecondarySchool}
            onChange={handleChange}
          />
          <Input
            label="Graduation Year"
            name="postSecondaryGradYear"
            type="number"
            value={formData.postSecondaryGradYear}
            onChange={handleChange}
          />
          <Input
            label="Qualification"
            name="postSecondaryQualification"
            value={formData.postSecondaryQualification}
            onChange={handleChange}
          />
          <Input
            label="Other Qualification"
            name="otherQualification"
            value={formData.otherQualification}
            onChange={handleChange}
          />
          <Input
            label="Year"
            name="otherQualificationYear"
            type="number"
            value={formData.otherQualificationYear}
            onChange={handleChange}
          />
          <Input
            label="Institution"
            name="otherQualificationSchool"
            value={formData.otherQualificationSchool}
            onChange={handleChange}
          />
        </FormSection>

        <FormSection title="Upload Picture">
          <div className="input-group passport-group">
            <label>Passport Photo</label>

            <div
              className="passport-preview"
              style={{ display: "flex", gap: 12, alignItems: "center" }}
            >
              {previewSrc ? (
                <img
                  src={previewSrc}
                  alt="Passport preview"
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    borderRadius: 6,
                    border: "1px solid #ddd",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 120,
                    height: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px dashed #ccc",
                    borderRadius: 6,
                    color: "#666",
                    fontSize: 12,
                  }}
                >
                  No passport
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={openFilePicker}
                    className="change-button"
                  >
                    Change
                  </button>

                  {originalPictureUrl && formData.picture instanceof File && (
                    <button
                      type="button"
                      onClick={revertToOriginalPicture}
                      className="revert-button"
                    >
                      Revert to original
                    </button>
                  )}
                </div>

                <small>
                  {formData.picture instanceof File
                    ? `New file: ${formData.picture.name}`
                    : originalPictureUrl
                      ? "Click Change to select a new passport"
                      : "No passport on record"}
                </small>
              </div>
            </div>

            <input
              ref={fileInputRef}
              id="picture"
              type="file"
              name="picture"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <small className="hint">
              Allowed formats: jpg, png. Max 1MB (backend dependent).
            </small>
          </div>
        </FormSection>

        <button type="submit" className="submit-button" disabled={submitting}>
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </form>
    </div>
  );
};

const FormSection = ({ title, children }) => (
  <div className="form-section">
    <h3>{title}</h3>
    <div className="form-grid">{children}</div>
  </div>
);

const Input = ({ label, name, type = "text", required = false, ...rest }) => (
  <div className="input-group">
    <label htmlFor={name}>
      {label}
      {required && <span className="required-star"> *</span>}
    </label>
    <input id={name} name={name} type={type} required={required} {...rest} />
  </div>
);

const Select = ({ label, name, options = [], required = false, ...rest }) => (
  <div className="input-group">
    <label htmlFor={name}>
      {label}
      {required && <span className="required-star"> *</span>}
    </label>
    <select id={name} name={name} required={required} {...rest}>
      <option value="">Select</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

const DOBField = ({
  label,
  name,
  value,
  onChange,
  formatDateForDisplay,
  formatDateForInput,
  required = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [tempDate, setTempDate] = useState(() => formatDateForInput(value));

  useEffect(() => {
    setTempDate(formatDateForInput(value));
  }, [value, formatDateForInput]);

  const startEdit = () => {
    setTempDate(formatDateForInput(value));
    setEditing(true);
  };

  const cancelEdit = () => {
    setTempDate(formatDateForInput(value));
    setEditing(false);
  };

  const saveEdit = () => {
    onChange(tempDate || "");
    setEditing(false);
  };

  return (
    <div className="input-group dob-group">
      {" "}
      <label htmlFor={name}>
        {label}
        {required && <span className="required-star"> *</span>}{" "}
      </label>
      {!editing ? (
        <div
          className="dob-display"
          style={{ display: "flex", gap: 12, alignItems: "center" }}
        >
          {" "}
          <span id={name} aria-live="polite">
            {value ? formatDateForDisplay(value) : "Not set"}{" "}
          </span>{" "}
          <button type="button" onClick={startEdit} className="link-button">
            Change{" "}
          </button>{" "}
        </div>
      ) : (
        <div
          className="dob-edit"
          style={{ display: "flex", gap: 8, alignItems: "center" }}
        >
          <input
            id={name}
            name={name}
            type="date"
            value={tempDate}
            onChange={(e) => setTempDate(e.target.value)}
            required={required}
          />{" "}
          <button type="button" onClick={saveEdit} className="save-button">
            Save{" "}
          </button>{" "}
          <button type="button" onClick={cancelEdit} className="cancel-button">
            Cancel{" "}
          </button>{" "}
        </div>
      )}{" "}
    </div>
  );
};

export default StudentBioDataForm;
